import { createSlice } from '@reduxjs/toolkit';
import toolBoxItemData from '../../public/toolboxitems.json';

const initialState = {
  items: [],
  customItems: [],
  searchResult: null,
};

const toolBoxItemSlice = createSlice({
  name: 'toolBoxItem',
  initialState,
  reducers: {
    // JSONファイルを読み込むアクションタイプとリデューサー関数を追加
    loadToolBoxData(state, action) {
      //[{tagName:xxx},....]
      // state.toolboxItemsにJSONファイルのデータをソートして代入
      const sortValues = toolBoxItemData.sort((a, b) => {
        return a.componentName.localeCompare(b.componentName);
      });

      state.items = sortValues;
    },
    loadToolBoxCustomData(state, action) {
      const payload = action.payload;

      if (Array.isArray(payload)) {
        // 1. まず有効なオブジェクトかつ、必要なプロパティを持つものだけに絞る (Sortでのクラッシュ防止)
        const validItems = payload.filter(item =>
          item &&
          typeof item === 'object' &&
          typeof item.componentName === 'string' // 名前がないとソートできないため弾く
        );

        // 2. 安全なデータのみでソート
        const sortValues = validItems.sort((a, b) => {
          return a.componentName.localeCompare(b.componentName);
        });

        // 3. 構造の正規化
        state.customItems = sortValues.map(item => ({
          groupName: "User Component",
          componentName: "Unknown",
          originScriptName: undefined,
          ...item,
          // 【重要】配列内の null/非文字列 を除去 (検索時の crash 防止)
          "filter-tag": Array.isArray(item["filter-tag"])
            ? item["filter-tag"].filter(t => t && typeof t === 'string')
            : [],
          component: (item.component && typeof item.component === 'object') ? item.component : {}
        }));
      } else {
        state.customItems = [];
      }
    },
    addToolBoxItem(state, action) {
      const item = action.payload;
      state.customItems.push(item);
    },
    updateToolBoxItem(state, action) {
      const newItem = action.payload;
      const originComponentName = newItem.originComponentName;
      // state.customItemsからcomponentNameに一致するアイテムのインデックスを探す
      const index = state.customItems.findIndex(item => item.componentName === originComponentName);

      // アイテムが見つかった場合、その位置を新しいアイテムで更新
      if (index !== -1) {
        delete newItem['originComponentName'];
        state.customItems[index] = newItem;
      }
    },
    deleteToolBoxItem(state, action) {
      const { componentName } = action.payload;
      // state.customItemsからcomponentNameに一致するアイテムを削除する
      state.customItems = state.customItems.filter(item => item.componentName !== componentName);

    },
    searchByTagName(state, action) {
      const tagName = action.payload;
      const foundItem = state.items.find(item => item.componentName === tagName);
      const foundCustomItem = state.customItems.find(item => item.componentName === tagName);
      state.searchResult = foundItem && foundItem.component || foundCustomItem && foundCustomItem.component || null;
    },

    // /**
    //  * ユーザースクリプトで定義されたカスタム要素をToolboxに同期します。
    //  * @param {object} state - 現在のstate
    //  * @param {object} action - payload: { scriptName: string, definedTags: string[] }
    //  */
    // syncUserScriptComponents(state, action) {
    //   const { scriptName, definedTags, comment} = action.payload;

    //   // 1. まず、このスクリプトに由来しない既存のアイテムだけを抽出する
    //   const existingItems = state.customItems.filter(item => item.originScriptName !== scriptName);

    //   // 2. 新しく追加するアイテムを生成する
    //   const newItems = definedTags
    //     .map(tagName => {
    //       // 3. 重複チェックを行う
    //       // システム定義アイテム、または「他のスクリプトに由来するアイテム」と重複していないか確認する
    //       const isDuplicate = state.items.some(item => item.componentName === tagName) ||
    //         existingItems.some(item => item.componentName === tagName);

    //       // 重複していなければ、新しいアイテムオブジェクトを返す
    //       if (!isDuplicate) {
    //         return {
    //           groupName: "User Script Component",
    //           componentName: tagName,
    //           "filter-tag": ["script,スクリプト"],
    //           component: {
    //             tagName: tagName,
    //             attributes: {},
    //             children: [],
    //             //explain: `Script: ${scriptName} で定義`,
    //             explain: comment || '',
    //           },
    //           originScriptName: scriptName
    //         };
    //       }
    //       // 重複している場合は null を返す
    //       return null;
    //     })
    //     .filter(item => item !== null); // null を除去して有効なアイテムのみの配列にする

    //   // 4. 既存のアイテムと新しいアイテムを結合し、ソートしてstateに設定する
    //   state.customItems = [...existingItems, ...newItems].sort((a, b) =>
    //     a.componentName.localeCompare(b.componentName)
    //   );
    // },

    /**
     * ユーザースクリプトで定義されたカスタム要素をToolboxに同期します。
     * @param {object} state - 現在のstate
     * @param {object} action - payload: { 
     *   originScriptName: string, // 由来のスクリプト名
     *   tagName: string,          // コンポーネントのタグ名
     *   comment: string           // コンポーネントのコメント
     * }
     */
    syncUserScriptComponent(state, action) {
      const { originScriptName, tagName, comment } = action.payload;

      // このコンポーネント(tagName)が既に存在するか確認
      const existingIndex = state.customItems.findIndex(item => item.componentName === tagName);

      const newItem = {
        groupName: "User Script Component",
        componentName: tagName,
        "filter-tag": ["script", "スクリプト"],
        component: {
          tagName: tagName,
          attributes: {},
          children: [],
          explain: comment || `Defined in ${originScriptName}`,
        },
        originScriptName: originScriptName // 由来スクリプト名を保持
      };

      if (existingIndex !== -1) {
        // 既に存在する場合: 更新
        // ただし、由来スクリプトが同じ場合のみ上書きする
        if (state.customItems[existingIndex].originScriptName === originScriptName) {
          state.customItems[existingIndex] = newItem;
        } else {
          // 由来スクリプトが異なる場合は何もしない（先に定義したものが優先）
          // もしくは、ここで警告を出すなどの処理も可能
        }
      } else {
        // 存在しない場合: 新規追加
        // システム定義のアイテムとも重複がないか最終チェック
        const isSystemDuplicate = state.items.some(item => item.componentName === tagName);
        if (!isSystemDuplicate) {
          state.customItems.push(newItem);
        }
      }

      // 最後にソート
      state.customItems.sort((a, b) => a.componentName.localeCompare(b.componentName));
    },

    /**
     * スクリプトが削除された際に、関連するToolboxItemを削除します。
     * @param {object} state - 現在のstate
     * @param {object} action - payload: { scriptName: string }
     */
    removeUserScriptComponentsByScriptName(state, action) {
      const { scriptName } = action.payload;
      state.customItems = state.customItems.filter(item => item.originScriptName !== scriptName);
    },
  },
});

// export function htmlToJson(htmlString) {
//   const parser = new DOMParser();
//   const doc = parser.parseFromString(htmlString, 'text/html');
//   const template = doc.querySelector('template');
//   const root = template ? template : doc.body;

//   function nodeToJson(node) {
//     // 実質的なコンテンツを持つテキストノードの処理
//     if (node.nodeType === Node.TEXT_NODE) {
//       const text = node.textContent.trim();
//       return text ? { text: text } : null; // 空白や改行のみの場合はnullを返す
//     }

//     const obj = {
//       tagName: node.tagName.toLowerCase(),
//       attributes: {},
//       children: []
//     };

//     if (node.content) {
//       obj.tagName = "template";
//       Array.from(node.content.childNodes).forEach(child => {
//         const childJson = nodeToJson(child);
//         if (childJson) { // nullでなければ追加
//           obj.children.push(childJson);
//         }
//       });
//     } else {
//       Array.from(node.childNodes).forEach(child => {
//         const childJson = nodeToJson(child);
//         if (childJson) { // nullでなければ追加
//           obj.children.push(childJson);
//         }
//       });
//     }

//     Array.from(node.attributes || []).forEach(attr => {
//       obj.attributes[attr.name] = attr.value;
//     });

//     return obj;
//   }

//   return nodeToJson(root);
// }

export function findByComponentName(state, componentName) {
  for (let component of state.toolboxItems.items) {
    if (component.componentName === componentName) {
      return component;
    }
  }
  return null;
}

export function findByCustomComponentName(state, componentName) {
  for (let component of state.toolboxItems.customItems) {
    if (component.componentName === componentName) {
      return component;
    }
  }
  return null;
}

export function htmlToJson(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // bodyの最初の「要素」を探し、なければheadの最初の「要素」を探す
  const root = doc.body.firstElementChild || doc.head.firstElementChild;

  if (!root) {
    return null;
  }

  function nodeToJson(node) {
    // 1. テキストノードの場合
    // トリムして空文字列になる場合は無視する
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      return text ? text : null;
    }

    // 2. 要素ノード以外（コメントノードなど）は無視する
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    // 3. 基本となるJSONオブジェクトを作成
    const obj = {
      tagName: node.tagName.toLowerCase(),
      attributes: {},
      children: [],
    };

    // 4. 属性を処理
    // idやexplainなどの特別な属性と、その他の属性を振り分ける
    Array.from(node.attributes).forEach(attr => {
      if (attr.name === 'id') {
        obj.domId = attr.value;
      } else if (attr.name === 'explain') {
        obj.explain = attr.value;
      } else {
        obj.attributes[attr.name] = attr.value;
      }
    });

    // 5. 子要素を処理
    // <template>タグの場合は .content から子要素を取得し、それ以外は通常通り取得する
    const childNodes = node.tagName === 'TEMPLATE'
      ? node.content.childNodes
      : node.childNodes;

    Array.from(childNodes).forEach(child => {
      const childJson = nodeToJson(child);
      if (childJson) { // nullでない場合のみ処理
        if (typeof childJson === 'string') {
          // テキストコンテンツは `text` プロパティに格納する
          // NOTE: 複数のテキストノードが要素内に混在する場合、最後のもので上書きされます。
          // 必要であれば配列にするなどの対応をご検討ください。
          obj.text = childJson;
        } else {
          // 要素ノードは `children` 配列に追加する
          obj.children.push(childJson);
        }
      }
    });

    return obj;
  }

  return nodeToJson(root);
}

export function jsonToHtml(json, depth = 0) {
  const indent = '  '.repeat(depth); // 現在の深さに基づいたインデント
  let element = `${indent}<${json.tagName}`;


  // ID属性があれば追加
  if (json.domId) {
    element += ` id="${json.domId}"`;
  }

  // 説明属性があれば追加
  if (json.explain) {
    element += ` explain="${json.explain}"`;
  }

  // 属性があれば追加
  if (json.attributes) {
    Object.keys(json.attributes).forEach((key) => {
      const value = json.attributes[key]; // 属性値を取得
      let attributeString = '';

      // 値が null や undefined でないことを確認
      if (value !== null && value !== undefined) {
        const valueStr = String(value); // 安全のため文字列に変換

        // 値にダブルクォートが含まれるかチェック
        if (valueStr.includes('"')) {
          // ダブルクォートが含まれる場合 -> シングルクォートで囲む
          // さらに値内部のシングルクォートをエスケープする必要があるか確認 (通常は不要かも)
          const escapedValue = valueStr.replace(/'/g, '\'');
          attributeString = ` ${key}='${escapedValue}'`;
        } else {
          // ダブルクォートが含まれない場合 -> ダブルクォートで囲む
          // 値内部のダブルクォートをエスケープ (念のため)
          const escapedValue = valueStr.replace(/"/g, '"');
          attributeString = ` ${key}="${escapedValue}"`;
        }
      } else {
        // 値が null や undefined の場合、属性を追加しない
      }
      element += attributeString;
    });
  }

  element += '>'; // すべての要素に対してタグを開く

  // テキストがあれば追加（テキストの前にインデントを追加）
  if (json.text) {
    element += `\n${indent}  ${json.text}\n`;
  }

  // 子要素があれば、それぞれに対してこの関数を再帰的に呼び出し（深さを1増やす）
  if (json.children && json.children.length > 0) {
    element += '\n'; // 子要素がある場合はここで改行
    json.children.forEach((child) => {
      element += jsonToHtml(child, depth + 1);
    });
  }

  if (json.children && json.children.length > 0 || json.text) {
    // 子要素またはテキストがある場合にはインデントを調整して改行
    element += `${indent}`;
  }

  element += `</${json.tagName}>\n`; // すべての要素を閉じる際はインデントを合わせる

  return element;
}

export function templateItemToHtml(json, depth = 0) {
  const indent = '  '.repeat(depth); // 現在の深さに基づいたインデント
  let element = `${indent}<${json.tagName}`;

  // 説明属性があれば追加
  if (json.domId) {
    element += ` id="${json.domId}"`;
  }

  // 説明属性があれば追加
  if (json.explain) {
    element += ` explain="${json.explain}"`;
  }

  // 属性があれば追加
  if (json.attributes) {
    Object.keys(json.attributes).forEach((key) => {
      if (key === 'explain') {
        if (!json.explain && json.attributes['explain']) {
          element += ` explain="${json.attributes['explain']}"`;
        }
      } else {
        if (json.attributes[key] === undefined || json.attributes[key] === null) {

        } else if (typeof json.attributes[key] === 'object') {
          element += ` ${key}="${json.attributes[key].join(' ')}"`;
        } else {
          element += ` ${key}="${json.attributes[key]}"`;
        }

      }
    });
  }

  // 子要素があるか、テキストがあるかに応じてタグを閉じる方法を変える
  if (json.children && json.children.length > 0 || json.text) {
    element += '>\n'; // 子要素またはテキストがある場合はここで改行

    // テキストがあれば追加（テキストの前にインデントを追加）
    if (json.text) {
      element += `${indent}  ${json.text}\n`;
    }

    // 子要素があれば、それぞれに対してこの関数を再帰的に呼び出し（深さを1増やす）
    if (json.children) {
      json.children.forEach((child) => {
        element += templateItemToHtml(child, depth + 1);
      });
    }

    element += `${indent}</${json.tagName}>\n`; // 要素を閉じる際はインデントを合わせる
  } else {
    // 子要素もテキストもない場合もタグを明示的に閉じる
    element += '></' + json.tagName + '>\n';
  }

  return element;
}


export function isValidDOMString(htmlString) {
  // DOMParserのインスタンスを作成
  const parser = new DOMParser();
  // 文字列をHTMLとして解析
  const doc = parser.parseFromString(htmlString, 'text/html');

  // 解析後のドキュメント内にパーサーエラーが含まれているかをチェック
  const isInvalid = doc.body.getElementsByTagName('parsererror').length > 0;

  // エラーがあればfalse、なければtrueを返す
  return !isInvalid;
}

export const { loadToolBoxData,
  loadToolBoxCustomData, searchByTagName, addToolBoxItem, updateToolBoxItem, deleteToolBoxItem,
  syncUserScriptComponent,
  removeUserScriptComponentsByScriptName } = toolBoxItemSlice.actions;
export default toolBoxItemSlice.reducer;
